import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_CLIENT } from '../../providers/supabase.provider';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private supabaseClient: SupabaseClient;

  constructor() {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  async getPermissions() {
    const { data, error } = await this.supabaseClient.from('permission_manage').select('*');
    if (error) {
      console.error('Error fetching permissions:', error.message);
    }
    return data;
  }

  async getPermissionById(permissionId: string) {
    const { data, error } = await this.supabaseClient.from('permission_manage').select('*').eq('id', permissionId);
    if (error) {
      console.error('Error fetching permissions:', error.message);
    }
    return data ? data[0] : null;
  }

  async getPermissionAdminsIdByPermissionTitle(permissionTitle: string) {
    const { data, error } = await this.supabaseClient.from('permission_manage').select('admins').eq('permission_title_en', permissionTitle);
    if (error) {
      console.error('Error fetching permissions:', error.message);
      return [];
    }
    if (!data || data.length === 0) {
      return [];
    }
    return data[0].admins || [];
  }

  async checkPermissionByAdminId(permissionTitle: string, adminId: string) {
    const { data: admin, error: adminError } = await this.supabaseClient
      .from('users')
      .select('is_admin,is_super_admin')
      .eq('id', adminId)
      .maybeSingle();

    if (adminError) {
      console.error('Error fetching admin user:', adminError.message);
      return false;
    }

    if (admin?.is_super_admin) {
      return true;
    }

    if (!admin?.is_admin) {
      return false;
    }

    const { data, error } = await this.supabaseClient
      .from('permission_manage')
      .select('admins')
      .eq('permission_title_en', permissionTitle)
      .maybeSingle();
    if (error) {
      console.error('Error fetching permissions:', error.message);
      return false;
    }

    const admins = data?.admins || [];
    return admins.includes(adminId);
  }

  async updatePermission(permissionId: string, permissionAdmins: string[]) {
    await this.requireCurrentUserIsSuperAdmin();

    const { error } = await this.supabaseClient.from('permission_manage').update({ admins: permissionAdmins }).eq('id', permissionId);
    if (error) {
      console.error('Error updating permission:', error.message);
      throw error;
    }
  }

  private async requireCurrentUserIsSuperAdmin() {
    const { data: authData, error: authError } = await this.supabaseClient.auth.getUser();
    if (authError || !authData.user) {
      throw new Error('Unauthorized');
    }

    const { data: user, error: userError } = await this.supabaseClient
      .from('users')
      .select('is_super_admin')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (userError) {
      throw userError;
    }

    if (!user?.is_super_admin) {
      throw new Error('Forbidden');
    }
  }

}
